import React, { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Car, Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get reCAPTCHA token (if available)
      let recaptchaToken = '';
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('admin_login');
        } catch (recaptchaError) {
          console.log('reCAPTCHA not configured:', recaptchaError);
        }
      }

      const endpoint = isRegister ? '/admin/register' : '/admin/login-v2';
      const payload = isRegister 
        ? formData 
        : { ...formData, recaptcha_token: recaptchaToken };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      login(response.data.token, response.data.admin);
      localStorage.setItem('adminToken', response.data.token);
      toast.success(isRegister ? 'Account created successfully!' : 'Welcome back!');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, formData, isRegister, login, navigate]);

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="bg-[#ff6b35] p-3 rounded-xl">
              <Car className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white font-['Exo_2']">We Have Your Car</h1>
          <p className="text-[#a8b8d0]">Admin Portal</p>
        </div>

        {/* Form Card */}
        <div className="bg-[#132238] rounded-xl p-8 border border-[#1a2d47]">
          <h2 className="text-xl font-bold text-white mb-6 text-center">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <label className="block text-[#a8b8d0] text-sm mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={isRegister}
                  placeholder="John Doe"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-3 text-white placeholder-[#a8b8d0]"
                  data-testid="admin-name-input"
                />
              </div>
            )}

            <div>
              <label className="block text-[#a8b8d0] text-sm mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a8b8d0]" size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="admin@example.com"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg pl-12 pr-4 py-3 text-white placeholder-[#a8b8d0]"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#a8b8d0] text-sm mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a8b8d0]" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg pl-12 pr-12 py-3 text-white placeholder-[#a8b8d0]"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a8b8d0] hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-accent w-full disabled:opacity-50"
              data-testid="admin-submit-btn"
            >
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          {/* reCAPTCHA notice */}
          <div className="mt-4 flex items-center justify-center gap-2 text-[#a8b8d0] text-xs">
            <Shield size={14} className="text-[#22c55e]" />
            Protected by reCAPTCHA
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#a8b8d0] hover:text-white text-sm transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
